import React, {useEffect, useRef} from "react";
import {abvNum, commafy, comparatorsPred, funcOrValOr, getAgo, getPct, objToArrK} from "../tools/CommonJS";
import {tailCheck, useStateEx} from "../tools/CommonReact";
import {colorWheel, DashCard} from "./CommonUIComponenets";
import {UIComboBox} from "../tools/UIComboBox";
import {genTooltip, hideTooltip, showTooltip} from "./AppContext";
import {Button, TextInput} from "../tools/Inputs";
import {dispDateTime, onTitle} from "../tools/CommonHtml";
import {enumOutClickModes} from "../tools/UIPopPanel";
import {icn} from "../tools/UIIcons";

export const ToolbarSortBy = ({sortBy, onChange, sortDefs}) =>
    <div data-test="tool-bar-sort" className="flex items-baseline">
        <div data-test="tool-bar-sort-title" className="text-xs text-tx-steel mx-1">Sort by</div>
        <UIComboBox
            selectMode className="border-none outline-0 bg-bg2 text-xs text-title px-1 py-0"
            autoWidth noCount vtProps={{rowHeight: 1.6}}
            stylePopPanel={{width: "11rem", height: (1.62 * objToArrK(sortDefs).length) + "rem"}}
            value={sortBy ?? Object.keys(sortDefs)[0]}
            {...{onChange}} field="sortBy" opts={objToArrK(sortDefs)}/>
    </div>;

// sorts list according to key of sortBy. optionally use kSort2nd as secondary sort.
export function sortListByDefs(list, sortBy, sortDefs, kSort2nd) {
    const sortFunc = key => sortDefs[key]?.fVal ?? (o => o?.[key]);
    let k = sortDefs[sortBy] ? sortBy : Object.keys(sortDefs)[0];
    if (kSort2nd && k !== kSort2nd)
        list?.sort(comparatorsPred(sortDefs[kSort2nd].sort, sortFunc(kSort2nd)));
    return list?.sort(comparatorsPred(sortDefs[k].sort, sortFunc(k)));
}

export const InputSearch = ({search, onChange, w = undefined, ...props}) =>
    <TextInput value={search} field="search" onChange={onChange} addClear classNameWrapper={w || "w-36"}
               btnRight={<div className="icon-search pr-1 text-sm"/>}
               className="rounded-full py-0.5 bg-transparent border border-title placeholder:text-xs w-36" placeholder="Search" {...props}/>;


export function MainTooltip({tip, className = "", children, doOnClick = undefined, opts = undefined, classNameWrapper = "", styleWrapper = {}}) {
    const vals = useRef({}).current;
    const show = e => (e?.stopPropagation(), showTooltip({rAnchor: e?.target, tip: tipBox, opts: {...doOnClick && {outClickMode: enumOutClickModes.propagateNoAnchor}, ...opts}}));
    const showClick = e => ((vals.tmShown = Date.now()), show(e));      // when initiated by a click, allow 500ms for animation to settle.
    const hide = e => !(Date.now() - vals.tmShown < 500) && ((delete vals.tmShown), hideTooltip());

    const tipBox = () =>
        <div data-test="main-tooltip" className={tailCheck(className, "p-2", "rounded-lg shadow-xl font-sans", "whitespace-pre-line [word-break:break-word]", "bg-bg1 text-desc")}
             onMouseEnter={e => show()} onMouseLeave={hide}>
            {funcOrValOr(tip)}
        </div>;

    return <div className={`inline-block ${doOnClick && "cursor-pointer"} ${classNameWrapper}`}
                onClick={doOnClick? showClick : undefined} {...{style : styleWrapper}}
                onMouseEnter={doOnClick? undefined : show}
                onMouseLeave={doOnClick? undefined : hide}>
        {children}
    </div>;
}

export const FormHint = ({tip, className = "", doOnClick = true}) => (
    <MainTooltip tip={tip} className="max-w-[30rem]" doOnClick={doOnClick}>
        <div data-test="form-hint" className={tailCheck(className, "text-icon", "icon-question", "opacity-80 hover:opacity-100", "transition-opacity")}/>
    </MainTooltip>
);

export const FormHintHover = ({tip, className}) =>
    <span {...genTooltip(tip)} data-test="form-hint"
         className={tailCheck(className, "text-icon", "icon-question", "opacity-80 hover:opacity-100", "transition-opacity")}/>;

export function TimeHint({timestamp, className}) {
    return <MainTooltip tip={`Data was last calculated at ${dispDateTime(timestamp)} and is calculated daily.`}
                        classNameWrapper="right-3 top-3 absolute" className="max-w-96" doOnClick>
        <div className={tailCheck(className, "w-3", "h-3", "flex items-center justify-center")}>
            {icn.clock}
        </div>
    </MainTooltip>
}

// obj should hold either a response object from server with potentially a cacheTime value,
//   or a boolean if to enable the button or disabled because it is already loading.
export const RefreshButton = ({obj, onRefresh}) => {
    const {state: st, setState} = useStateEx({});
    useEffect(() => {
        if (st.pressed) setState({pressed: false});
    }, [obj]);

    return <Button data-test="attack-vector-refresh" className="px-2 text-sm text-prm-light gap-2"
                   onClick={e => (setState({pressed: true}), onRefresh())} disabled={!obj || obj.calcState > 0 || st.pressed}
                   {...onTitle(!obj ? "Loading..." : obj.cacheTime &&
                       (e => `Last update: ${getAgo(obj.cacheTime)}${obj.calcState > 0 ? ". Server is already working on a refresh..." : ""}`))}>
        {(st.pressed || !obj || obj.calcState > 0) &&
            <div className={`animate-[spin_1.5s_linear_infinite] border-2 border-subtitle2 border-r-transparent rounded-full w-2.5 h-2.5`}/>}
        Refresh
    </Button>;
};

export const DispLastUpdated = ({obj, recalcPeriod, className = ""}) => obj?.cacheTime &&
    <MainTooltip classNameWrapper={className} className="bg-bg2 text-desc whitespace-pre p- px-2 py-1" tip={() =>
        `Last calculation: ${
            getAgo(obj.cacheTime)} (${
            dispDateTime(obj.cacheTime)})${
            obj.calcState > 0 ? 
                "\nServer is currently recalculating the data..." : 
                recalcPeriod?
                    `\nServer is scheduled to recalculate once every ${typeof recalcPeriod === "string"? recalcPeriod : "hour"}.` : ""}`
    }>
        <div className="px-2 text-xs text-subtitle1 flex items-center gap-1 cursor-default">
            Last calc.: {obj?.cacheTime && getAgo(obj.cacheTime)}
            {obj.calcState > 0 &&
                <div className={`animate-[spin_1.5s_linear_infinite] border-2 border-subtitle2 border-r-transparent rounded-full w-2.5 h-2.5`}/>}
        </div>
    </MainTooltip>;


/**
 * @param defs object-array of h, k, c
 * @param states tuples of key and number
 * */
export function StatsScale({states, statesToShow, defs, className, wRem, showNum = false}) {
    const statsArr = statesToShow?.length ? states.filter(([status,]) => statesToShow.includes(status)) : states;
    const total = statsArr?.reduce((acc, [_, sum]) => acc + sum, 0);


    return <div className={tailCheck(className, "flex items-center", "w-28")}>
        {statsArr?.map(([currStat, currSum], iCurr) =>
            <MainTooltip styleWrapper={{
                flex: "0 1 " + getPct(currSum, total),
                background: `${defs[currStat]?.c || colorWheel[iCurr]}`,
                border: `${defs[currStat]?.c || colorWheel[iCurr]} solid 1px`,
            }} classNameWrapper={`px-1 inline-grid animate-[slideIn] first:rounded-l last:rounded-r min-h-2.5 text-center ${
                defs[currStat]?.contrastText || "text-white"} ${defs[currStat]?.bg}`}
                         key={currStat} tip={_ => legendTooltip({statsArr, currStat, defs})}>
                {showNum && <div className="leading-4 whitespace-nowrap">{showNum && abvNum(currSum)}</div>}
            </MainTooltip>)}
    </div>;
}


export const legendTooltip = ({statsArr, currStat, defs}) =>
    <DashCard className="bg-bg3 w-44 text-xs flex flex-col gap-1 p-2">
        {statsArr.map(([status, sum], i) =>
            <div key={status} className={`${currStat === status && "font-bold text-title"} flex items-center justify-between w-full`}>
                <div className="flex items-center gap-2">
                    <div style={{...!defs[status]?.bg && {background: defs[status]?.c || colorWheel[i]}}}
                         className={`w-3 aspect-square rounded-sm ${defs[status]?.bg}`}/>
                    {defs[status]?.h}
                </div>
                <div>{commafy(sum)}</div>
            </div>)}
    </DashCard>;